import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    DataGridPro,
    GridToolbarColumnsButton, GridToolbarContainer,
    GridToolbarFilterButton,
} from '@mui/x-data-grid-pro';
import apiService from '../api/apiService';
import HazardApiService from '../api/HazardApiService';
import HazardEditMenuButton from './Hazard/HazardEditMenuButton';
import Box from '@mui/material/Box';
import ConfirmDeleteDialogHazard from './Modal/ConfirmDeleteHazard';
import { Button } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { updateHazardFilters, updateHazardColumns } from '../store/correcttrackSlice';
import { downloadCsv, toGridQuery } from '../utils/gridQuery';

const hazardApiService = new HazardApiService();

// Stable tokens for risk level. Kept separate from translated display so the
// CSS class lookup (and any other internal comparison) doesn't break in
// non-English locales. The server computes and sends these same tokens.
const RISK_LEVEL = {
    LOW: 'low',
    MEDIUM: 'medium',
    HIGH: 'high',
    VERY_HIGH: 'veryHigh',
};

const RISK_LEVEL_CLASS = {
    [RISK_LEVEL.LOW]: 'Low',
    [RISK_LEVEL.MEDIUM]: 'Medium',
    [RISK_LEVEL.HIGH]: 'High',
    [RISK_LEVEL.VERY_HIGH]: 'VeryHigh',
};

const RISK_LEVEL_I18N_KEY = {
    [RISK_LEVEL.LOW]: 'riskLevel.low',
    [RISK_LEVEL.MEDIUM]: 'riskLevel.medium',
    [RISK_LEVEL.HIGH]: 'riskLevel.high',
    [RISK_LEVEL.VERY_HIGH]: 'riskLevel.veryHigh',
};

// Typing in the filter panel now costs a round trip, so hold off until the user pauses.
const FILTER_DEBOUNCE_MS = 400;

const Hazards = () => {
    const { t } = useTranslation();
    const [hazards, setHazards] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [refreshToken, setRefreshToken] = useState(0);
    const [currentHazard, setCurrentHazard] = useState(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [sortModel, setSortModel] = useState([]);
    const [currentFilters, setCurrentFilters] = useState(useSelector((state) => state.correcttrack.hazardFilters));
    // The model the grid renders updates on every keystroke; the one below is what we query with.
    const [appliedFilters, setAppliedFilters] = useState(currentFilters);
    const [currentVisibleFields, setCurrentVisibleFields] = useState(useSelector((state) => state.correcttrack.hazardColumns));

    const dispatch = useDispatch();
    const debounceRef = useRef(null);

    function CustomToolbar () {
        return (
            <GridToolbarContainer>
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                {/* Replaces GridToolbarExport, which would only ever see the loaded page. */}
                <Button
                    size="small"
                    startIcon={<FileDownloadOutlinedIcon />}
                    disabled={exporting}
                    onClick={onExportClick}
                >
                    {t('common.export')}
                </Button>
            </GridToolbarContainer>
        );
    }

    function updateFilters (filterModel) {
        setCurrentFilters(filterModel);
        dispatch(updateHazardFilters(filterModel));

        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        debounceRef.current = setTimeout(() => {
            setPage(0);
            setAppliedFilters(filterModel);
        }, FILTER_DEBOUNCE_MS);
    }

    function updateVisibleColumns (columnsModel) {
        setCurrentVisibleFields(columnsModel);
        dispatch(updateHazardColumns(columnsModel));
    }

    function updateSort (newSortModel) {
        setPage(0);
        setSortModel(newSortModel);
    }

    const renderRiskLevel = (params) => {
        const token = params.value;
        if (!token) return '';
        const key = RISK_LEVEL_I18N_KEY[token];
        return key ? t(key) : token;
    };

    // Risk levels filter as a fixed set rather than free text, so the user picks a translated
    // label while the server still matches on the stable token behind it.
    const riskLevelOptions = useMemo(() => Object.values(RISK_LEVEL).map((value) => ({
        value,
        label: t(RISK_LEVEL_I18N_KEY[value]),
    })), [t]);

    const columns = useMemo(() => [
        { field: 'hzId', headerName: t('fieldName.riskId'), type: 'number', flex: 1 },
        { field: 'hzOrganizationName', headerName: t('fieldName.organization'), flex: 1 },
        { field: 'hzProject', headerName: t('fieldName.project'), flex: 1 },
        { field: 'hzRiskSource', headerName: t('fieldName.riskSource'), minWidth: 110, flex: 1 },
        { field: 'hzDepartmentName', headerName: t('fieldName.dept'), flex: 1 },
        { field: 'hzProcess', headerName: t('fieldName.process'), flex: 1 },
        { field: 'hzJob', headerName: t('fieldName.job'), flex: 1 },
        { field: 'hzTask', headerName: t('fieldName.task'), minWidth: 90, flex: 1 },
        { field: 'hzEquipmentId', headerName: t('fieldName.equipmentId'), flex: 1 },
        { field: 'hzRiskDescription', headerName: t('fieldName.riskDescription'), flex: 1 },
        {
            field: 'hzRiskScoreUncontrolled',
            headerName: t('fieldName.riskScoreUncontrolled'),
            type: 'number',
            flex: 1.5,
        },
        {
            field: 'hzRiskLevelUncontrolled',
            headerName: t('fieldName.riskLevelUncontrolled'),
            type: 'singleSelect',
            valueOptions: riskLevelOptions,
            flex: 1.5,
            renderCell: renderRiskLevel,
        },
        {
            field: 'hzRiskScoreControlled',
            headerName: t('fieldName.riskScoreControlled'),
            type: 'number',
            flex: 1.5,
        },
        {
            field: 'hzRisKLevelControlled',
            headerName: t('fieldName.riskLevelControlled'),
            type: 'singleSelect',
            valueOptions: riskLevelOptions,
            flex: 1.5,
            renderCell: renderRiskLevel,
        },
        {
            field: 'significant',
            headerName: t('fieldName.significant'),
            type: 'boolean',
            flex: 1,
            renderCell: (params) => (params.row.significant ? t('common.yes') : t('common.no')),
        },
        {
            field: 'edit',
            headerName: t('common.edit'),
            flex: 0.5,
            filterable: false,
            sortable: false,
            renderCell: (params) => {
                return <HazardEditMenuButton
                    params={params}
                    setShowDeleteDialog={setShowDeleteDialog}
                    setCurrentSelectedHazard={setCurrentHazard}
                />;
            },
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [t, riskLevelOptions]);

    const dialogTitle = () => {
        return t('hazardsPage.confirmDeleteTitle', { id: currentHazard?.hzId ?? '' });
    };

    const dialogLabel = () => {
        return t('hazardsPage.confirmDeleteLabel', { id: currentHazard?.hzId ?? '' });
    };

    const onDeleteHazardClick = (deleteValue) => {
        if (deleteValue) {
            apiService.deleteData('api/Hazard/' + currentHazard.hzId).then(() => {
                setRefreshToken((token) => token + 1);
            });
        }

        setShowDeleteDialog(false);
    };

    const buildQuery = useCallback(() => toGridQuery({
        page,
        pageSize,
        sortModel,
        filterModel: appliedFilters,
    }), [page, pageSize, sortModel, appliedFilters]);

    const onExportClick = async () => {
        setExporting(true);
        try {
            // The export endpoint ignores page/pageSize, so the file covers every row
            // matching the current filters rather than just what's on screen.
            const blob = await hazardApiService.exportHazards(buildQuery());
            if (blob) {
                downloadCsv(blob, 'risks.csv');
            }
        } finally {
            setExporting(false);
        }
    };

    useEffect(() => {
        let cancelled = false;
        setLoading(true);

        hazardApiService.searchHazards(buildQuery()).then((result) => {
            if (cancelled) {
                return;
            }

            setHazards(result?.items ?? []);
            setRowCount(result?.totalCount ?? 0);
            setLoading(false);
        });

        return () => {
            cancelled = true;
        };
    }, [buildQuery, refreshToken]);

    useEffect(() => () => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
    }, []);

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100vh - var(--ct-header-h, 56px))',
            width: '100%',
            '& .VeryHigh': {
                backgroundColor: '#FF0000',
                color: '#1a3e72',
            },
            '& .High': {
                backgroundColor: '#FFA500',
                color: '#1a3e72',
            },
            '& .Medium': {
                backgroundColor: '#FFFF00',
                color: '#1a3e72',
            },
            '& .Low': {
                backgroundColor: '#00FF00',
                color: '#1a3e72',
            },
        }}>
            <Button
                variant="contained"
                href="/Hazard"
                sx={{
                    alignSelf: 'flex-end',
                    marginBottom: '16px',
                    marginTop: '16px',
                    width: 'fit-content'
                }}
            >
                {t('hazardsPage.createNewRisk')}
            </Button>
            <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
            <DataGridPro
                rows={hazards}
                columns={columns}
                loading={loading}
                pagination
                paginationMode="server"
                sortingMode="server"
                filterMode="server"
                rowCount={rowCount}
                page={page}
                onPageChange={(newPage) => setPage(newPage)}
                pageSize={pageSize}
                onPageSizeChange={(newPageSize) => {
                    setPage(0);
                    setPageSize(newPageSize);
                }}
                sortModel={sortModel}
                onSortModelChange={updateSort}
                components={{
                    Toolbar: CustomToolbar,
                }}
                filterModel={currentFilters}
                onFilterModelChange={(newFilterModel) => updateFilters(newFilterModel)}
                columnVisibilityModel={currentVisibleFields}
                onColumnVisibilityModelChange={(newColumnsModel) => updateVisibleColumns(newColumnsModel)}
                getRowHeight={() => 'auto'}
                rowsPerPageOptions={[10, 25, 50, 100, 200]}
                getCellClassName={(params) => {
                    if ((params.field !== 'hzRiskLevelUncontrolled' && params.field !== 'hzRisKLevelControlled') || params.value == null) {
                        return '';
                    }
                    return RISK_LEVEL_CLASS[params.row[params.field]] ?? '';
                }}
                getRowId={(row) => row.hzId}
            />
            </Box>

            <ConfirmDeleteDialogHazard
                id="confirm-delete"
                keepMounted
                value={currentHazard}
                open={showDeleteDialog}
                onClose={onDeleteHazardClick}
                deleteTitle={dialogTitle()}
                deleteLabel={dialogLabel()}
            ></ConfirmDeleteDialogHazard>
        </Box>
    );
};
export default Hazards;